import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { DemandBadge, ContactFlag, ArchivedBadge } from "../../components/founder/ui";
import BusinessActions from "../../components/founder/BusinessActions";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { ArrowLeft, Link2, RotateCcw } from "lucide-react";

import OverviewTab from "./tabs/OverviewTab";
import IntakeTab from "./tabs/IntakeTab";
import InvestigationTab from "./tabs/InvestigationTab";
import EvidenceTab from "./tabs/EvidenceTab";
import DiagnosisTab from "./tabs/DiagnosisTab";
import BlueprintTab from "./tabs/BlueprintTab";
import ImplementationTab from "./tabs/ImplementationTab";
import HistoryTab from "./tabs/HistoryTab";

const TABS = [
  ["overview", "Overview", OverviewTab],
  ["intake", "Intake", IntakeTab],
  ["investigation", "Investigation", InvestigationTab],
  ["evidence", "Evidence", EvidenceTab],
  ["diagnosis", "Diagnosis", DiagnosisTab],
  ["blueprint", "Blueprint", BlueprintTab],
  ["implementation", "Implementation", ImplementationTab],
  ["history", "History", HistoryTab],
];

export default function Workspace() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [statuses, setStatuses] = useState([]);

  const load = useCallback(() => api.get(`/businesses/${bid}/workspace`).then((r) => setWs(r.data)), [bid]);
  useEffect(() => { load(); api.get("/meta").then((r) => setStatuses(r.data.statuses)); }, [load]);

  const changeStatus = async (s) => {
    await api.patch(`/businesses/${bid}/status`, { status: s });
    toast.success(`Status → ${s}`);
    load();
  };

  const sendIntake = async () => {
    const { data } = await api.post(`/businesses/${bid}/intake/send`);
    const url = `${window.location.origin}${data.path}`;
    try { await navigator.clipboard.writeText(url); toast.success("Assessment link copied to clipboard"); }
    catch { toast.info(url); }
    load();
  };

  const reactivate = async () => {
    await api.post(`/businesses/${bid}/reactivate`, {});
    toast.success("Business reactivated");
    load();
  };

  if (!ws) return <div className="p-8 text-lr-leather">Loading…</div>;
  const b = ws.business;

  return (
    <div className="min-h-screen">
      {b.archived && (
        <div className="px-5 md:px-8 py-3 bg-lr-leather/12 border-b hairline flex flex-wrap items-center justify-between gap-3" data-testid="archived-banner">
          <div className="flex flex-wrap items-center gap-2 text-sm text-lr-charcoal">
            <ArchivedBadge />
            <span>Archived{b.archived_at ? ` on ${b.archived_at.slice(0, 10)}` : ""}{b.disposition ? `. ${b.disposition}.` : "."}</span>
            <ContactFlag status={b.future_contact_status} />
            {b.future_contact_status === "follow_up_after_date" && b.future_contact_date && (
              <span className="text-lr-clay">Follow up after {b.future_contact_date}</span>
            )}
          </div>
          <Button onClick={reactivate} size="sm" data-testid="reactivate-btn" className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Reactivate
          </Button>
        </div>
      )}
      <div className="p-5 md:p-8 border-b hairline bg-lr-card/50">
        <button onClick={() => navigate("/app/businesses")} className="text-sm text-lr-leather hover:text-lr-olive flex items-center gap-1 mb-3" data-testid="back-to-businesses">
          <ArrowLeft className="h-4 w-4" /> All businesses
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-serif text-3xl text-lr-charcoal">{b.business_name}</h1>
              {b.is_demo && <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded-[3px] bg-lr-leather/15 text-lr-leather">DEMO</span>}
              {b.paid
                ? <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded-[3px] bg-lr-olive/15 text-lr-olive" data-testid="workspace-paid-badge">PAID{b.paid_amount ? ` · $${(b.paid_amount / 100).toFixed(0)}` : ""}</span>
                : <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded-[3px] bg-lr-clay/10 text-lr-clay/80" data-testid="workspace-unpaid-badge">UNPAID</span>}
              <DemandBadge value={b.preliminary_demand_safety} />
            </div>
            <p className="text-lr-leather mt-1">{b.owner_name || "Not recorded"}{b.primary_trade ? ` · ${b.primary_trade}` : ""}{b.location ? ` · ${b.location}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={b.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-52 bg-white/70" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={sendIntake} data-testid="send-intake-btn" variant="outline" className="rounded-[4px] border-lr-olive/40 text-lr-olive hover:bg-lr-olive/10">
              <Link2 className="h-4 w-4 mr-1.5" /> {ws.intake ? "Copy Link" : "Send Intake"}
            </Button>
            <BusinessActions business={b} onChange={load} onDeleted={() => navigate("/app/businesses")} />
          </div>
        </div>
      </div>

      <div className="p-5 md:p-8">
        <Tabs defaultValue="overview">
          <TabsList className="flex flex-wrap h-auto bg-transparent gap-1 mb-6 justify-start">
            {TABS.map(([k, label]) => (
              <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}
                className="data-[state=active]:bg-lr-olive data-[state=active]:text-lr-cream rounded-[4px] px-4 py-2 text-lr-charcoal/80">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map(([k, label, Comp]) => (
            <TabsContent key={k} value={k}><Comp ws={ws} bid={bid} reload={load} sendIntake={sendIntake} /></TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
